// The dashboard used to start at a hardcoded March 2026 and run to whatever the
// API had. Leadership now picks how far to look: the current fiscal year, the
// next 18 months, or a custom month range. These helpers turn that choice into
// the `from`/`to` the queries take, label it, and remember it, so the decision
// is testable without rendering App.
//
// A custom range cannot start before Mar 2026: that is where the books begin,
// so an earlier month could only ever show an empty chart.
//
// Months are 'YYYY-MM-01' strings and are compared as strings. Month maths runs
// on integer year/month, never Date arithmetic: a Date crossing a timezone
// would quietly slip a month at the boundary.

export type PresetKind = 'fiscal-year' | 'rolling-18'
export type WindowKind = PresetKind | 'custom'
export type DataWindow = { kind: WindowKind; from: string; to: string }
export type CustomRange = { from: string; to: string }
// Invariant: kind === 'custom' implies custom !== null. `custom` is also kept
// while a preset is applied, so the custom pickers can prefill from it.
export type WindowChoice = { kind: WindowKind; custom: CustomRange | null }

// ZooKeep's fiscal year starts in March (Rusvin, 2026-10-21), so today's
// "current fiscal year" is Mar 2026 – Feb 2027.
export const FISCAL_YEAR_START_MONTH = 3
export const DEFAULT_WINDOW_KIND: PresetKind = 'fiscal-year'
export const WINDOW_STORAGE_KEY = 'zk-finance.window'
export const CUSTOM_STORAGE_KEY = 'zk-finance.window.custom'
export const CUSTOM_EARLIEST = '2026-03-01'

const ROLLING_MONTHS = 18
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function parts(month: string): { year: number; month: number } {
  return { year: Number(month.slice(0, 4)), month: Number(month.slice(5, 7)) }
}

function format(year: number, month: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`
}

export function addMonths(month: string, count: number): string {
  const { year, month: m } = parts(month)
  const index = year * 12 + (m - 1) + count
  return format(Math.floor(index / 12), (index % 12) + 1)
}

// "Now" is the Tokyo calendar month, not the browser's: the business closes its
// books in Tokyo, so a reader abroad at the turn of a month sees the same
// window as the team does.
export function tokyoMonth(now: Date): string {
  const text = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
  }).format(now)
  const match = /^(\d{4})-(\d{2})/.exec(text)
  if (match === null) throw new Error(`Unexpected Tokyo month format: ${text}`)
  return `${match[1]}-${match[2]}-01`
}

export function resolveWindow(kind: PresetKind, now: Date): DataWindow {
  return resolveAt(kind, tokyoMonth(now))
}

function resolveAt(kind: PresetKind, current: string): DataWindow {
  if (kind === 'rolling-18') {
    return { kind, from: current, to: addMonths(current, ROLLING_MONTHS - 1) }
  }
  const { year, month } = parts(current)
  const startYear = month >= FISCAL_YEAR_START_MONTH ? year : year - 1
  const from = format(startYear, FISCAL_YEAR_START_MONTH)
  return { kind, from, to: addMonths(from, 11) }
}

// The months the window covers beyond the last month a series has. The data end
// is derived from each chart's own last row, never hardcoded: the forecast
// horizon moves whenever the forecast sheet is extended.
export function monthsAfter(lastMonth: string, to: string): string[] {
  const months: string[] = []
  for (let next = addMonths(lastMonth, 1); next <= to; next = addMonths(next, 1)) months.push(next)
  return months
}

export function noDataMonths(dataEnd: string, window: DataWindow): string[] {
  const first = addMonths(dataEnd, 1)
  return monthsAfter(addMonths(first > window.from ? first : window.from, -1), window.to)
}

export function monthLabel(month: string): string {
  const { year, month: m } = parts(month)
  return `${MONTH_NAMES[m - 1] ?? ''} ${year}`
}

export function rangeLabel(from: string, to: string): string {
  return `${monthLabel(from)} – ${monthLabel(to)}`
}

// How long a drafted range is, counting both ends, so the reader sees the span
// before loading it.
export function monthSpanLabel(range: CustomRange): string {
  const start = parts(range.from)
  const end = parts(range.to)
  const count = (end.year - start.year) * 12 + (end.month - start.month) + 1
  return count === 1 ? '1 month' : `${count} months`
}

export function windowFor(choice: WindowChoice, now: Date): DataWindow {
  return windowAt(choice, tokyoMonth(now))
}

// The window for `choice` as of the Tokyo month `current`. The page keeps that
// month in state, so a preset stays put between loads and moves only when
// turnedMonth says the month has changed.
export function windowAt(choice: WindowChoice, current: string): DataWindow {
  if (choice.kind === 'custom' && choice.custom !== null) {
    return { kind: 'custom', ...choice.custom }
  }
  return resolveAt(choice.kind === 'rolling-18' ? 'rolling-18' : DEFAULT_WINDOW_KIND, current)
}

// A tab left open across a Tokyo month turn (or 1 March, for the fiscal year)
// would keep showing the old preset window, and Reload would reuse it. Returns
// the month the page should move to, or null when `shown` still holds or the
// choice is a custom range, which never moves.
export function turnedMonth(choice: WindowChoice, shown: string, now: Date): string | null {
  if (choice.kind === 'custom') return null
  const current = tokyoMonth(now)
  return current === shown ? null : current
}

export function customRangeProblem(range: CustomRange): string | null {
  if (range.from < CUSTOM_EARLIEST) return `The earliest start is ${monthLabel(CUSTOM_EARLIEST)}.`
  if (range.to < range.from) return 'The end month is before the start month.'
  return null
}

// Years offered by the custom pickers: from the books' first year to five past
// `dataEnd`, the last month shown in the current window (the current Tokyo year
// when that is unknown: before data loads, after a failed load, on the IT-costs
// page). The picker widens this to reach any year already in its draft.
export function customYears(dataEnd: string | null, now: Date): number[] {
  const first = parts(CUSTOM_EARLIEST).year
  const last = parts(dataEnd ?? tokyoMonth(now)).year + 5
  return Array.from({ length: last - first + 1 }, (_, i) => first + i)
}

export function inWindow(month: string, window: DataWindow): boolean {
  return month >= window.from && month <= window.to
}

function defaultStorage(): Storage | null {
  // Reading the global can itself throw (blocked site data) and is absent under
  // SSR / the preview renderer.
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

// A remembered preference is a convenience: garbage or unavailable storage must
// fall back to the default, never break the page.
const FALLBACK_CHOICE: WindowChoice = { kind: DEFAULT_WINDOW_KIND, custom: null }
const MONTH_START = /^\d{4}-\d{2}-01$/

function parseCustom(text: string | null | undefined): CustomRange | null {
  if (typeof text !== 'string') return null
  try {
    const raw: unknown = JSON.parse(text)
    if (typeof raw !== 'object' || raw === null) return null
    const { from, to } = raw as Record<string, unknown>
    if (typeof from !== 'string' || typeof to !== 'string') return null
    if (!MONTH_START.test(from) || !MONTH_START.test(to)) return null
    const range = { from, to }
    return customRangeProblem(range) === null ? range : null
  } catch {
    return null
  }
}

export function readWindowChoice(
  storage: Pick<Storage, 'getItem'> | null = defaultStorage(),
): WindowChoice {
  try {
    const kind = storage?.getItem(WINDOW_STORAGE_KEY)
    const custom = parseCustom(storage?.getItem(CUSTOM_STORAGE_KEY))
    if (kind === 'custom') return custom === null ? FALLBACK_CHOICE : { kind, custom }
    if (kind === 'fiscal-year' || kind === 'rolling-18') return { kind, custom }
    return { kind: DEFAULT_WINDOW_KIND, custom }
  } catch {
    return FALLBACK_CHOICE
  }
}

export function writeWindowChoice(
  choice: WindowChoice,
  storage: Pick<Storage, 'setItem'> | null = defaultStorage(),
): void {
  try {
    storage?.setItem(WINDOW_STORAGE_KEY, choice.kind)
    if (choice.custom !== null) storage?.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(choice.custom))
  } catch {
    // Not remembering is fine; the choice still applies for this visit.
  }
}
